import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Box, Text } from '@atlaskit/primitives/compiled';
import Select, {
	type InputActionMeta,
	type OptionsType,
	type StylesConfig,
} from '@atlaskit/select';
import { layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { performHttpFetch } from '@atlassian/conversation-assistant-utils/httpUtils';

import type { FieldBaseProps } from '../common/types';
import { LruCache } from '../utils/LruCache';

import type { JiraSprintField_Platform$key } from './__generated__/JiraSprintField_Platform.graphql';

const messages = defineMessages({
	selectLabel: {
		id: 'jira.jira.jira-platform-fields.sprint-field.label',
		defaultMessage: 'Select Sprint',
		description: 'Accessible label for the sprint select dropdown',
	},
	none: {
		id: 'jira.jira.jira-platform-fields.sprint-field.none',
		defaultMessage: 'None',
		description:
			'Value for the sprint field when no sprint is selected and the sprint field is not editable',
	},
});

const styles = cssMap({
	container: {
		width: '100%',
	},
	readOnlyContainer: {
		display: 'flex',
		alignItems: 'center',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		width: '100%',
		height: '40px',
	},
});

type SprintOption = {
	value: string;
	label: string;
};

type SprintPickerPayload = {
	allMatches: Array<{
		id: number;
		name: string;
	}>;
};

const selectStylesIframe: StylesConfig<SprintOption, false> = {
	menu: (base) => ({ ...base, zIndex: layers.modal() }),
	menuPortal: (base) => ({ ...base, zIndex: layers.modal() }),
};

const getOptionValue = (opt: SprintOption) => opt.value;
const getOptionLabel = (opt: SprintOption) => opt.label;

const SPRINT_PICKER_BASE_URL = '/rest/greenhopper/1.0/sprint/picker?query=';
const SPRINT_BY_ID_BASE_URL = '/rest/agile/1.0/sprint/';

const fetchSprints = (query = ''): Promise<SprintOption[]> =>
	performHttpFetch<SprintPickerPayload>({
		path: `${SPRINT_PICKER_BASE_URL}${encodeURIComponent(query)}`,
		options: { method: 'GET', headers: { Accept: 'application/json' } },
	}).then((data) =>
		(data.allMatches ?? []).map((s) => ({
			value: String(s.id),
			label: s.name,
		})),
	);

const sprintByIdCache = LruCache<Promise<SprintOption>>(20);
const sprintByIdInFlight = new Map<string, Promise<SprintOption | null>>();

const fetchSprintById = (id: string): Promise<SprintOption | null> => {
	const cached = sprintByIdCache.get(id);
	if (cached) {
		return cached.value;
	}
	const inFlight = sprintByIdInFlight.get(id);
	if (inFlight) {
		return inFlight;
	}
	const req = performHttpFetch<{ id: number; name: string }>({
		path: `${SPRINT_BY_ID_BASE_URL}${encodeURIComponent(id)}`,
		options: { method: 'GET', headers: { Accept: 'application/json' } },
	})
		.then((data) => {
			const opt: SprintOption = {
				value: String(data.id),
				label: data.name ?? String(data.id),
			};
			sprintByIdCache.set(id, Promise.resolve(opt));
			sprintByIdInFlight.delete(id);
			return opt;
		})
		.catch(() => {
			sprintByIdInFlight.delete(id);
			return null;
		});
	sprintByIdInFlight.set(id, req);
	return req;
};

const SEARCH_DEBOUNCE_MS = 300;

export type Props = FieldBaseProps & {
	field: JiraSprintField_Platform$key;
	onChange?: (newValue: unknown) => void;
};

export const JiraSprintField: React.MemoExoticComponent<
	({ field, fieldValue, isFieldEditable, onChange }: Props) => React.JSX.Element
> = memo(({ field, fieldValue, isFieldEditable = true, onChange }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const data = useFragment<JiraSprintField_Platform$key>(
		graphql`
			fragment JiraSprintField_Platform on JiraSprintField {
				__typename
				fieldConfig {
					isEditable
					isRequired
				}
			}
		`,
		field,
	);

	const { isRequired, isEditable: isEditableFromConfig } = data.fieldConfig ?? {};
	const isEditable = !!(isEditableFromConfig ?? true) && isFieldEditable;

	const selectRef = useRef<{ blur(): void; focus(): void } | null>(null);
	const [options, setOptions] = useState<OptionsType<SprintOption>>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
	const hasSearchedRef = useRef(false);

	useEffect(() => {
		let cancelled = false;
		setIsLoading(true);
		fetchSprints()
			.then((opts) => {
				if (!cancelled) {
					setOptions(opts);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setOptions([]);
				}
			})
			.finally(() => {
				if (!cancelled) {
					setIsLoading(false);
				}
			});
		return () => {
			cancelled = true;
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, []);

	const currentId = fieldValue != null ? String(fieldValue) : null;
	const [hydratedOption, setHydratedOption] = useState<SprintOption | null>(null);

	const optionValueSet = useMemo(() => new Set(options.map((opt) => opt.value)), [options]);

	const hydratedIdRef = useRef<string | null>(null);
	useEffect(() => {
		if (!currentId) {
			setHydratedOption(null);
			hydratedIdRef.current = null;
			return;
		}
		if (hydratedIdRef.current === currentId) {
			return;
		}
		hydratedIdRef.current = currentId;
		setHydratedOption(null);
		let cancelled = false;
		fetchSprintById(currentId).then((opt) => {
			if (!cancelled && opt) {
				setHydratedOption(opt);
			}
		});
		return () => {
			cancelled = true;
		};
	}, [currentId]);

	const selected = useMemo(() => {
		if (!currentId) {
			return null;
		}
		if (optionValueSet.has(currentId)) {
			return options.find((opt) => opt.value === currentId) ?? null;
		}
		return hydratedOption;
	}, [options, optionValueSet, currentId, hydratedOption]);

	const handleChange = useCallback(
		(option: SprintOption | null) => {
			onChange?.(option?.value ?? null);
			selectRef.current?.blur();
		},
		[onChange],
	);

	const handleMenuOpen = useCallback(() => {
		setIsMenuOpen(true);
	}, []);

	const handleMenuClose = useCallback(() => {
		setIsMenuOpen(false);
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}
		if (hasSearchedRef.current) {
			hasSearchedRef.current = false;
			setIsLoading(true);
			fetchSprints()
				.then((opts) => {
					setOptions(opts);
				})
				.catch(() => {
					setOptions([]);
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	}, []);

	const handleInputChange = useCallback((inputValue: string, actionMeta: InputActionMeta) => {
		if (actionMeta.action !== 'input-change') {
			return;
		}
		hasSearchedRef.current = true;
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}
		setIsLoading(true);
		debounceTimerRef.current = setTimeout(() => {
			fetchSprints(inputValue)
				.then((opts) => {
					setOptions(opts);
				})
				.catch(() => {
					setOptions([]);
				})
				.finally(() => {
					setIsLoading(false);
				});
		}, SEARCH_DEBOUNCE_MS);
	}, []);

	if (!isEditable) {
		return (
			<Box xcss={styles.container}>
				<Box xcss={styles.readOnlyContainer}>
					<Text color={selected?.label ? 'color.text' : 'color.text.subtle'} maxLines={1}>
						{selected?.label || formatMessage(messages.none)}
					</Text>
				</Box>
			</Box>
		);
	}

	return (
		<Box xcss={styles.container}>
			<Select<SprintOption>
				ref={selectRef as React.Ref<any>}
				label={formatMessage(messages.selectLabel)}
				appearance="subtle"
				options={options}
				isLoading={isLoading}
				value={selected}
				onChange={handleChange}
				onInputChange={handleInputChange}
				onMenuOpen={handleMenuOpen}
				onMenuClose={handleMenuClose}
				getOptionValue={getOptionValue}
				getOptionLabel={getOptionLabel}
				isRequired={!!isRequired}
				isClearable={isMenuOpen && !isRequired}
				isMulti={false}
				isSearchable
				menuPlacement="auto"
				menuPosition="fixed"
				styles={selectStylesIframe}
			/>
		</Box>
	);
});
